import { useState } from "react";
import { Check, Undo2 } from "lucide-react";
import { Button } from "@/components/form/Button";
import { cn } from "@/utils/helpers";
import { kitBareFieldClass } from "../../components/kit/Field";

/** Optional since backend #131: the client says what to change in comments. */
const REASON_MIN = 0;

/**
 * A Guest's answer on a creative brief the team sent them, in the team's brief
 * workspace: approve it, or send it back with what should change.
 */
export default function GuestBriefDecision({
  busy,
  onApprove,
  onRequestChanges,
}: {
  busy: boolean;
  onApprove: () => void;
  onRequestChanges: (reason: string) => void;
}) {
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState("");
  const tooShort = reason.trim().length < REASON_MIN;

  if (!declining) {
    return (
      <div className="flex flex-wrap gap-2">
        <Button size="small" icon={Check} isDisabled={busy} onPress={onApprove}>
          Approve creative brief
        </Button>
        <Button size="small" variant="secondary" buttonStyle="outline" icon={Undo2} isDisabled={busy} onPress={() => setDeclining(true)}>
          Request changes
        </Button>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-2 @[44rem]/package:w-[28rem]">
      <label className="flex flex-col gap-1.5">
        <span className="tt-read-s text-co-text2">What should change?</span>
        <textarea
          rows={3}
          dir="auto"
          value={reason}
          maxLength={2000}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Tell the team what to change before production…"
          className={cn(kitBareFieldClass, "w-full resize-y px-3.5 py-2.5 text-base leading-relaxed sm:text-sm")}
        />
      </label>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="tt-label uppercase text-co-faint">
          {tooShort ? `At least ${REASON_MIN} characters` : "Ready to send"}
        </span>
        <div className="flex gap-2">
          <Button size="small" variant="secondary" buttonStyle="outline" isDisabled={busy} onPress={() => setDeclining(false)}>
            Cancel
          </Button>
          <Button
            size="small"
            isDisabled={busy || tooShort}
            onPress={() => {
              onRequestChanges(reason.trim());
              setDeclining(false);
              setReason("");
            }}
          >
            Send back
          </Button>
        </div>
      </div>
    </div>
  );
}
